import { Component, Input, Output, EventEmitter, OnInit, OnDestroy } from '@angular/core';
import { MiniGameStudent } from '../../../interfaces/mini-game.interface';
import { environment } from 'src/environments/environment';
import { GameAudioService } from '../../../_services/game-audio.service';

@Component({
  selector: 'app-spin-wheel',
  templateUrl: './spin-wheel.component.html',
  styleUrls: ['./spin-wheel.component.scss']
})
export class SpinWheelComponent implements OnInit, OnDestroy {
  @Input() students: MiniGameStudent[] = [];
  @Input() currLang: any = 'en';
  @Output() studentSelected = new EventEmitter<MiniGameStudent>();

  isSpinning = false;
  currentRotation = 0;
  bulbs: number[] = [];
  bulbLit = false;
  private spinAudio: HTMLAudioElement | null = null;
  private spinTimeout: any = null;
  private bulbInterval: any = null;

  // Alternating segment colors (dark/light orange-gold)
  private segmentColors = ['#D4700A', '#F5A623'];

  constructor(private gameAudioService: GameAudioService) {}

  ngOnInit(): void {
    // Generate bulb positions around the ring
    const count = 24;
    this.bulbs = Array.from({ length: count }, (_, i) => (i * 360) / count);
    // Alternate bulb lighting
    this.bulbLit = true;
    this.bulbInterval = setInterval(() => {
      this.bulbLit = !this.bulbLit;
    }, 600);
  }

  ngOnDestroy(): void {
    if (this.spinTimeout) clearTimeout(this.spinTimeout);
    if (this.bulbInterval) clearInterval(this.bulbInterval);
    this.gameAudioService.stop(this.spinAudio);
  }

  get segmentAngle(): number {
    return this.students.length ? 360 / this.students.length : 45;
  }

  spin(): void {
    if (this.isSpinning || !this.students.length) return;
    this.isSpinning = true;
    this.playSpinSound();

    const targetIndex = Math.floor(Math.random() * this.students.length);
    const segAngle = this.segmentAngle;
    const targetAngle = 360 - (targetIndex * segAngle + segAngle / 2);
    const fullSpins = (8 + Math.floor(Math.random() * 3)) * 360;
    this.currentRotation += fullSpins + targetAngle - (this.currentRotation % 360);

    this.spinTimeout = setTimeout(() => {
      this.isSpinning = false;
      this.studentSelected.emit(this.students[targetIndex]);
    }, 7000);
  }

  private playSpinSound(): void {
    this.gameAudioService.stop(this.spinAudio);
    this.spinAudio = this.gameAudioService.play('assets/game-mode/sound-effects/spinWheelSoundEffect.mp3');
  }

  // SVG pie slice path for each segment (400x400 viewBox, center at 200,200)
  getSegmentPath(index: number): string {
    const cx = 200, cy = 200, r = 198;
    const angle = this.segmentAngle;
    const startAngle = index * angle - 90; // -90 so 0 starts at top
    const endAngle = startAngle + angle;
    const startRad = (startAngle * Math.PI) / 180;
    const endRad = (endAngle * Math.PI) / 180;

    const x1 = cx + r * Math.cos(startRad);
    const y1 = cy + r * Math.sin(startRad);
    const x2 = cx + r * Math.cos(endRad);
    const y2 = cy + r * Math.sin(endRad);

    const largeArc = angle > 180 ? 1 : 0;
    return `M${cx},${cy} L${x1},${y1} A${r},${r} 0 ${largeArc} 1 ${x2},${y2} Z`;
  }

  getSegmentFill(index: number): string {
    return this.segmentColors[index % 2];
  }

  getLabelTransform(index: number): string {
    const angle = this.segmentAngle;
    const rotation = index * angle + angle / 2;
    return `translateX(-50%) rotate(${rotation}deg)`;
  }

  getImageUrl(student: MiniGameStudent): string {
    if (student.image_path) {
      const filename = student.image_path.split('/').pop() || '';
      if (filename !== 'avatar.png') {
        return environment.img_url + student.image_path;
      }
    }
    return 'assets/images/avatar.png';
  }

  getAvatarSize(): number {
    const count = this.students.length;
    if (count <= 8) return 70;
    if (count <= 12) return 65;
    if (count <= 16) return 55;
    if (count <= 20) return 36;
    return 32;
  }

  getNameSize(): number {
    const count = this.students.length;
    if (count <= 8) return 14;
    if (count <= 12) return 12;
    if (count <= 16) return 11;
    if (count <= 20) return 10;
    return 9;
  }

  getStudentLabel(student: MiniGameStudent): string {
    return this.currLang === 'ar'
      ? student.name.ar.split(' ')[0]
      : student.name.en.split(' ')[0];
  }

  // Position bulbs centered in the outer ring band (percentage-based for responsiveness)
  getBulbX(angleDeg: number): number {
    const rad = ((angleDeg - 90) * Math.PI) / 180;
    return 50 + 48 * Math.cos(rad);
  }

  getBulbY(angleDeg: number): number {
    const rad = ((angleDeg - 90) * Math.PI) / 180;
    return 50 + 48 * Math.sin(rad);
  }
}
